import {
	ReportImage,
	ReportImageAlign,
	ReportImageCrop,
	ReportImageRequest,
	ReportImageSize,
	ReportImageStyle
} from './report-images.service';
import { ReportContentItem } from './report-settings.service';
import {
	REPORT_COVER_ID,
	placedAfter,
	placedAtEnd,
	reportPlaces
} from './report-placement';

/**
 * Where a report's pictures go, and what shape they are drawn in.
 *
 * One set of rules for the three surfaces that draw a report — the editor, the
 * PDF export and the share link — so a picture placed, sized or cropped in the
 * editor is where, and as big, and framed as, the reader left it in the file
 * they download and the link they send.
 */

/** The server's bounds: the settings' `images`, and a picture's `title`. */
export const MAX_REPORT_IMAGES = 20;
export const MAX_REPORT_IMAGE_TITLE_LENGTH = 200;

/** The upload route's limit, checked here first so the answer is instant. */
export const MAX_REPORT_IMAGE_UPLOAD_BYTES = 5 * 1024 * 1024;
export const REPORT_IMAGE_UPLOAD_TYPES = [
	'image/png',
	'image/jpeg',
	'image/webp'
];

/** The looks a reader can pick, the default first. Null is that default. */
export const REPORT_IMAGE_STYLES: ReadonlyArray<{
	id: ReportImageStyle | null;
	label: string;
}> = [
	{ id: null, label: 'Auto' },
	{ id: 'photo', label: 'Photo' },
	{ id: 'illustration', label: 'Illustration' },
	{ id: '3d', label: '3D' },
	{ id: 'abstract', label: 'Abstract' }
];

/** How many pictures "Illustrate this report" puts beside its sections. */
const ILLUSTRATION_ACCENTS = 3;

/** Only a picture the model drew carries the "AI image" label. */
export function isGeneratedImage(image: ReportImage): boolean {
	return image.origin !== 'uploaded';
}

/** Beside its caption, rather than across the whole width. */
export function isImageBeside(image: Pick<ReportImage, 'align'>): boolean {
	return image.align === 'left' || image.align === 'right';
}

/** How much of its card's width the picture itself takes. */
export function imageFrameWidth(
	image: Pick<ReportImage, 'align' | 'size'>
): number {
	if (!isImageBeside(image)) {
		return 1;
	}
	return { s: 1 / 3, m: 1 / 2, l: 2 / 3 }[image.size ?? 'm'];
}

/**
 * The shape to have a picture drawn in, for the frame it will sit in. A card
 * is the PDF page's shape; a picture beside its caption is a narrower slice
 * of it, and a 16:9 drawing cropped into that slice would lose most of itself.
 */
export function imageAspectRatio(
	image: Pick<ReportImage, 'align' | 'size'>
): NonNullable<ReportImageRequest['aspect_ratio']> {
	const width = imageFrameWidth(image);
	if (width === 1) {
		return '16:9';
	}
	return width > 0.5 ? '4:3' : '1:1';
}

/** The frame's crop, or the centre at no zoom — how every picture was drawn. */
export function imageCrop(image: Pick<ReportImage, 'crop'>): ReportImageCrop {
	return image.crop ?? { x: 50, y: 50, zoom: 1 };
}

/**
 * The pictures drawn straight after `id` — the cover or one section — in the
 * order the report holds them. Nothing, for an item that is not on the page.
 */
export function imagesAfter(
	images: ReportImage[],
	id: string,
	shownIds: ReadonlySet<string>
): ReportImage[] {
	return placedAfter(images, id, shownIds);
}

/**
 * The pictures drawn at the end, after the charts: every one placed nowhere —
 * where all of them went before they could be placed — and every one placed
 * after a section that is not on the page, so that switching a section off
 * never takes a picture out of the report with it.
 */
export function imagesAtEnd(
	images: ReportImage[],
	shownIds: ReadonlySet<string>
): ReportImage[] {
	return placedAtEnd(images, shownIds);
}

/** The ids a document draws a picture after: its cover and shown sections. */
export function shownImagePlaces(
	sections: ReportContentItem[]
): ReadonlySet<string> {
	return new Set(reportPlaces(sections));
}

/**
 * The picture without its empty fields. A report is read back without its
 * nulls, so a copy holding one would not compare equal to the copy it saved,
 * and the save that follows a lost answer would call its own write a change
 * made elsewhere.
 */
export function compactImage(image: ReportImage): ReportImage {
	return Object.fromEntries(
		Object.entries(image).filter(
			([, value]) => value !== undefined && value !== null
		)
	) as unknown as ReportImage;
}

/**
 * A new picture for a card already in the report: the new drawing or upload,
 * in the card's place and shape. The crop goes — it was chosen for the
 * picture being replaced — and the style is the new picture's own.
 */
export function replacePicture(
	card: ReportImage,
	picture: ReportImage
): ReportImage {
	return compactImage({
		slot: card.slot,
		title: picture.title,
		key: picture.key,
		style: picture.style,
		origin: picture.origin,
		after: card.after,
		align: card.align,
		size: card.size
	});
}

/** Clipped by code point, as the server counts: never half an emoji. */
export function clipImageTitle(title: string): string {
	return Array.from(title.trim())
		.slice(0, MAX_REPORT_IMAGE_TITLE_LENGTH)
		.join('')
		.trim();
}

/** Where a new picture goes: a slot no picture holds. */
export function newImageCardSlot(): string {
	return `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** One picture "Illustrate this report" will have drawn, and where. */
export interface PlannedPicture {
	heading: string;
	after: string;
	align: ReportImageAlign;
	size?: ReportImageSize;
}

/**
 * What "Illustrate this report" draws: a cover picture when the cover has
 * none, and up to three more after sections spread through the report,
 * counting the ones already there. Only what is missing, so a second press —
 * or a report illustrated already — never pays for the same pictures twice,
 * and never more than `room`, what is left of the report's twenty.
 *
 * The first section is passed over: it opens straight after the cover and its
 * picture, and a picture after it would be the second in a row. The side ones
 * alternate, so the document does not lean one way.
 */
export function planIllustration(
	coverTitle: string,
	sections: ReportContentItem[],
	images: ReportImage[],
	room: number
): PlannedPicture[] {
	const placed = new Set(images.map(image => image.after));
	const plan: PlannedPicture[] = [];
	const coverHeading = clipImageTitle(coverTitle);
	if (!placed.has(REPORT_COVER_ID) && coverHeading) {
		plan.push({
			heading: coverHeading,
			after: REPORT_COVER_ID,
			align: 'full'
		});
	}

	const illustrated = sections.filter(section => placed.has(section.id));
	const open = sections.slice(1).filter(section => !placed.has(section.id));
	const wanted = Math.min(
		Math.max(ILLUSTRATION_ACCENTS - illustrated.length, 0),
		open.length
	);
	for (let pick = 0; pick < wanted; pick++) {
		const section = open[Math.floor(((pick + 0.5) * open.length) / wanted)];
		const heading = clipImageTitle(section.title || section.name);
		if (heading) {
			plan.push({
				heading,
				after: section.id,
				align: pick % 2 ? 'left' : 'right',
				size: 'm'
			});
		}
	}
	return plan.slice(0, Math.max(room, 0));
}
